import { ReactNode } from "react";
import { PlusIcon } from "@heroicons/react/24/outline";
import { useNavigate } from "@tanstack/react-router";
import { useTranslations } from "use-intl";
import { formatCurrency } from "@/lib/Formatters/formatCurrency";
import { useGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { IconMenuActions } from "@/lib/Components/Actions/IconMenuActions";
import {
  VehicleListItem,
  vehicleListQuery,
} from "@/app/Vehicles/GraphQL/vehicleListQuery";
import { useVehicleActions } from "@/app/Vehicles/Hooks/useVehicleActions";
import { VehicleIcon } from "@/app/Vehicles/Components/VehicleIcon";
import { VehicleCreateDialog } from "@/app/Vehicles/Components/VehicleCreateDialog";

export function VehicleGrid({
  supplierId,
  addLabel = "Add vehicle",
}: {
  supplierId: string;
  addLabel?: string;
}) {
  const getActions = useVehicleActions();
  const { open: openCreate } = useDialog(VehicleCreateDialog);

  const { data } = useGqlQuery(vehicleListQuery, {
    first: 100,
    supplier_id: supplierId,
    archived: false,
  });

  const vehicles = data?.vehicles.data ?? [];

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {vehicles.map((vehicle) => (
        <VehicleCard
          key={vehicle.id}
          vehicle={vehicle}
          actions={<IconMenuActions actions={getActions(vehicle)} />}
        />
      ))}

      <button
        type="button"
        onClick={() =>
          openCreate({
            initialValues: { supplier: { connect: supplierId } },
          })
        }
        className="flex min-h-[148px] flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-gray-200 p-4 text-sm font-semibold text-gray-500 transition hover:border-indigo-300 hover:bg-indigo-50/40 hover:text-indigo-600 focus:outline-none focus-visible:border-indigo-400"
      >
        <PlusIcon className="size-6" />
        {addLabel}
      </button>
    </div>
  );
}

function VehicleCard({
  vehicle,
  actions,
}: {
  vehicle: VehicleListItem;
  actions: ReactNode;
}) {
  const navigate = useNavigate();
  const t = useTranslations("vehicle");
  const tTable = useTranslations("vehicle.table");

  return (
    <div className="relative flex flex-col rounded-lg border border-gray-200 bg-white transition hover:border-gray-300 hover:shadow-sm">
      <div className="absolute right-2 top-2">{actions}</div>

      <button
        type="button"
        onClick={() =>
          navigate({
            to: ".",
            search: (prev: any) => ({ ...prev, vehicleId: vehicle.id }),
          })
        }
        className="flex flex-1 flex-col p-4 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40"
      >
        <div className="flex items-center gap-3 pr-6">
          <VehicleIcon
            className="size-12 shrink-0 rounded-md"
            model={vehicle}
          />
          <div className="min-w-0">
            <div className="truncate font-semibold text-gray-900">
              {vehicle.name}
            </div>
            {vehicle.code ? (
              <div className="truncate text-xs text-gray-500">
                {vehicle.code}
              </div>
            ) : null}
          </div>
        </div>

        <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
          <Detail label={tTable("type")} value={t(`type.${vehicle.type}`)} />
          <Detail
            label={tTable("transmission")}
            value={t(`transmission.${vehicle.transmission}`)}
          />
          <Detail
            label={tTable("unit")}
            value={t(`hire_unit_type.${vehicle.hire_unit_type}`)}
          />
          <Detail
            label={tTable("extra_day_rate")}
            value={formatCurrency(
              vehicle.extra_hire_unit_rate,
              vehicle.supplier.currency,
            )}
          />
        </dl>
      </button>
    </div>
  );
}

function Detail({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-gray-500">{label}</dt>
      <dd className="mt-0.5 font-medium text-gray-900">{value}</dd>
    </div>
  );
}
